import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People,
  Assessment,
  Description,
  CheckCircle,
  Rule,
  School,
  AutoAwesome,
} from '@mui/icons-material';
import { PageHeader, Loading, ErrorMessage, StatusBadge, ScoreChip } from './common';
import { apiService } from '../services/api';

const Dashboard = () => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [stats, setStats] = React.useState({
    totalAspects: 0,
    aspectsWithGuidelines: 0,
    aspectsWithFewShots: 0,
    totalGuidelines: 0,
    totalFewShots: 0,
    totalEvaluations: 0,
    recentSubmissions: [],
  });

  const fetchDashboardData = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [aspectsRes, guidelinesRes, fewShotsRes, submissionsRes] = await Promise.allSettled([
        apiService.aspects.list(7),
        apiService.guidelines.list(7),
        apiService.fewShotExamples.list(7),
        apiService.submissions.list({ page_size: 5 }),
      ]);

      const aspects = aspectsRes.status === 'fulfilled' ? (aspectsRes.value.data || []) : [];
      const guidelines = guidelinesRes.status === 'fulfilled' ? (guidelinesRes.value.data.guidelines || []) : [];
      const fewShots = fewShotsRes.status === 'fulfilled' ? (fewShotsRes.value.data.few_shot_examples || []) : [];
      const submissions = submissionsRes.status === 'fulfilled' ? (submissionsRes.value.data.results || []) : [];

      const aspectIds = new Set(aspects.map((a) => a.id));
      const guidelineAspectIds = new Set(guidelines.map((g) => g.aspect));
      const fewShotAspectIds = new Set(fewShots.map((f) => f.aspect));

      const aspectsWithGuidelines = aspects.filter((a) => guidelineAspectIds.has(a.id)).length;
      const aspectsWithFewShots = aspects.filter((a) => fewShotAspectIds.has(a.id)).length;

      setStats({
        totalAspects: aspects.length,
        aspectsWithGuidelines,
        aspectsWithFewShots,
        totalGuidelines: guidelines.length,
        totalFewShots: fewShots.length,
        totalEvaluations: submissions.length,
        recentSubmissions: submissions,
      });
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const statCards = [
    {
      title: 'Active Aspects',
      value: stats.totalAspects,
      description: 'Evaluation dimensions configured',
      icon: <DashboardIcon color="primary" />,
      color: 'primary',
    },
    {
      title: 'Guidelines',
      value: stats.totalGuidelines,
      description: `${stats.aspectsWithGuidelines} of ${stats.totalAspects} aspects covered`,
      icon: <Rule color="success" />,
      color: 'success',
    },
    {
      title: 'Few-Shot Examples',
      value: stats.totalFewShots,
      description: `${stats.aspectsWithFewShots} of ${stats.totalAspects} aspects covered`,
      icon: <School color="info" />,
      color: 'info',
    },
    {
      title: 'Recent Submissions',
      value: stats.totalEvaluations,
      description: 'Total evaluation runs',
      icon: <Assessment color="secondary" />,
      color: 'secondary',
    },
  ];

  const quickActions = [
    { label: 'Manage Candidates', path: '/candidates', icon: <People />, color: 'primary' },
    { label: 'View Submissions', path: '/submissions', icon: <Assessment />, color: 'success' },
    { label: 'Guidelines & Examples', path: '/guidelines', icon: <Rule />, color: 'info' },
    { label: 'Analytics', path: '/analytics', icon: <DashboardIcon />, color: 'secondary' },
  ];

  const guidelineCoverage = stats.totalAspects > 0
    ? Math.round((stats.aspectsWithGuidelines / stats.totalAspects) * 100)
    : 0;

  const fewShotCoverage = stats.totalAspects > 0
    ? Math.round((stats.aspectsWithFewShots / stats.totalAspects) * 100)
    : 0;

  if (loading) {
    return <Loading message="Loading dashboard..." size="large" />;
  }

  return (
    <Box>
      <PageHeader
        title="Assessment Dashboard"
        subtitle="Overview of evaluation system configuration and recent activity"
      />

      {error && <ErrorMessage message={error} onRetry={fetchDashboardData} />}

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statCards.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    {stat.title}
                  </Typography>
                  <Avatar sx={{ bgcolor: 'action.hover', color: 'primary.main' }}>{stat.icon}</Avatar>
                </Box>
                <Typography variant="h4" fontWeight={700} color={`${stat.color}.main`} gutterBottom>
                  {stat.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stat.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Coverage & Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Evaluation Coverage
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    <Rule fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Guidelines Coverage
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {guidelineCoverage}%
                  </Typography>
                </Box>
                <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${guidelineCoverage}%`, bgcolor: 'success.main', borderRadius: 4, transition: 'width 0.3s' }} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {stats.aspectsWithGuidelines} of {stats.totalAspects} aspects have guidelines
                </Typography>
              </Box>

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    <School fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Few-Shot Examples Coverage
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {fewShotCoverage}%
                  </Typography>
                </Box>
                <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${fewShotCoverage}%`, bgcolor: 'info.main', borderRadius: 4, transition: 'width 0.3s' }} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {stats.aspectsWithFewShots} of {stats.totalAspects} aspects have few-shot examples
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                {quickActions.map((action) => (
                  <Grid item xs={12} sm={6} key={action.label}>
                    <Button
                      component={Link}
                      to={action.path}
                      variant="outlined"
                      color={action.color}
                      startIcon={action.icon}
                      fullWidth
                      sx={{ justifyContent: 'flex-start', py: 1.5, borderRadius: 2 }}
                    >
                      {action.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Submissions */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Recent Submissions
              </Typography>
              {stats.recentSubmissions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No recent submissions
                </Typography>
              ) : (
                <List dense>
                  {stats.recentSubmissions.map((submission, idx) => (
                    <React.Fragment key={submission.id}>
                      <ListItem
                        component={Link}
                        to={`/candidate/${submission.id}`}
                        sx={{ px: 0, textDecoration: 'none', color: 'inherit' }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'action.hover' }}>
                            <Description fontSize="small" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={submission.candidate_id ? `Candidate ${submission.candidate_id}` : `Submission ${submission.id}`}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <StatusBadge status={submission.status} />
                              {submission.evaluations_count > 0 && (
                                <Chip label={`${submission.evaluations_count} evals`} size="small" variant="outlined" />
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {new Date(submission.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {idx < stats.recentSubmissions.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              )}
              <Box sx={{ mt: 1, textAlign: 'right' }}>
                <Button component={Link} to="/submissions" size="small">
                  View All Submissions
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;